import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaUserPlus, FaArrowLeft, FaShareAlt, FaCopy, FaTimes, FaTable, FaPlus, FaSignOutAlt, FaPen } from 'react-icons/fa';
import { MdDeleteForever } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import leagueService from '../services/leagueService';
import invitationService from '../services/invitationService';
import tournamentService from '../services/tournamentService';
import TeamBadge from './TeamBadge';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import '../styles/LeagueDetails.css';

const slugifyStandingsGroup = (name) =>
	String(name).toLowerCase().trim().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, '_');

const translateGroupPart = (part, t) => {
	const trimmed = String(part).trim();

	// Specific named groups (e.g. "Group Stage") take priority over the generic
	// "Group <letter>" handling below, otherwise they'd be mangled (Group Stage -> "Skupina STAGE").
	const slug = slugifyStandingsGroup(trimmed);
	const MISSING = '__missing__';
	const specific = t(`standings.groups.${slug}`, { defaultValue: MISSING });
	if (specific !== MISSING) {
		return specific;
	}

	const groupMatch = trimmed.match(/^group\s+(.+)$/i);
	if (groupMatch) {
		const prefix = t('standings.groups.groupPrefix', { defaultValue: 'Group' });
		return `${prefix} ${groupMatch[1].toUpperCase()}`;
	}

	return trimmed;
};

const formatStandingsGroup = (rawName, t) => {
	if (!rawName) return '';
	const parts = String(rawName).split(',').map(s => s.trim()).filter(Boolean);
	if (parts.length < 2) {
		return translateGroupPart(rawName, t);
	}
	const [leaguePart, ...rest] = parts;
	const subPart = rest.join(', ');
	return `${leaguePart} - ${translateGroupPart(subPart, t)}`;
};

const LeagueHeader = ({ league, userName }) => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery({ maxWidth: 768 });

	// Stavy pro modální okno
	const [showInviteModal, setShowInviteModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showStandingsModal, setShowStandingsModal] = useState(false);
	const [inviteLink, setInviteLink] = useState('');
	const [isLoadingLink, setIsLoadingLink] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [standings, setStandings] = useState([]);
	const [isLoadingStandings, setIsLoadingStandings] = useState(false);
	const [isFabOpen, setIsFabOpen] = useState(false);
	const [showLeaveModal, setShowLeaveModal] = useState(false);
	const [isLeaving, setIsLeaving] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editName, setEditName] = useState('');
	const [isSavingEdit, setIsSavingEdit] = useState(false);

	const sportType = (league.tournament?.sportType || '').toUpperCase();
	const isFootball = sportType === 'FOOTBALL';

	// Turnaj jednoho týmu (např. "AC Sparta Praha 2026-2027") – jeden tým hraje ve všech zápasech,
	// ligová tabulka pro něj nedává smysl
	const tournamentMatches = league.tournament?.matches || [];
	const isSingleTeamTournament =
		tournamentMatches.length > 0 &&
		[tournamentMatches[0].homeTeam?.id, tournamentMatches[0].awayTeam?.id]
			.filter(Boolean)
			.some((teamId) =>
				tournamentMatches.every(
					(m) => m.homeTeam?.id === teamId || m.awayTeam?.id === teamId
				)
			);

	// 1. Otevření modálu a načtení odkazu
	const handleOpenInviteModal = async () => {
		setShowInviteModal(true);
		setIsLoadingLink(true);
		setInviteLink('');

		try {
			const link = await invitationService.getInviteByLeagueId(league.id);
			setInviteLink(link);
		} catch (error) {
			console.error(error);
			toast.error(t('leagueDetails.inviteLinkFailed', 'Nepodařilo se získat odkaz'));
			setShowInviteModal(false);
		} finally {
			setIsLoadingLink(false);
		}
	};

	// 2. Nativní sdílení
	const handleNativeShare = async () => {
		if (navigator.share) {
			try {
				await navigator.share({
					title: t('leagues.inviteTitle', { defaultValue: `Pozvánka do ligy ${league.name}` }),
					text: t('leagues.inviteText', { defaultValue: `Přidej se do mé ligy "${league.name}" na Scoreo!` }),
					url: inviteLink,
				});
				setShowInviteModal(false);
			} catch (error) {
				console.log('Share dismissed or failed', error);
			}
		} else {
			handleCopyLink();
		}
	};

	// 3. Kopírování
	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(inviteLink);
			toast.success(t('leagueDetails.inviteLinkCopied'));
			setShowInviteModal(false);
		} catch (error) {
			toast.error(t('leagueDetails.inviteLinkFailed'));
		}
	};

	const handleOpenStandingsModal = async () => {
		const tournamentId = league.tournament?.id;
		if (!tournamentId) return;
		setShowStandingsModal(true);
		setIsLoadingStandings(true);
		setStandings([]);
		try {
			const data = await tournamentService.getStandings(tournamentId);
			setStandings(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error(error);
			toast.error(t('leagueDetails.standingsFailed', { defaultValue: 'Nepodařilo se načíst tabulku' }));
			setShowStandingsModal(false);
		} finally {
			setIsLoadingStandings(false);
		}
	};

	const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            await leagueService.deleteLeague(league.id);
            toast.success(t('leagueDetails.leagueDeleted'));
            window.location.href = '/dashboard';
        } catch (err) {
            toast.error(t('leagueDetails.deleteFailed'));
            console.error(err);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

	const ownerUsername = league.owner ? league.owner.username : (league.creator ? league.creator.username : null);
	const isOwner = ownerUsername === userName;

	const totalPlayers = league.totalPlayers ?? league.players?.length ?? 0;
	const canLeave =
		league.isMember &&
		league.status !== 'FINISHED' &&
		!(isOwner && totalPlayers === 1);

	const handleOpenEditModal = () => {
		setEditName(league.name || '');
		setShowEditModal(true);
	};

	const handleConfirmEdit = async () => {
		const trimmed = editName.trim();
		if (!trimmed) {
			toast.error(t('leagueDetails.editNameRequired', { defaultValue: 'Název nesmí být prázdný' }));
			return;
		}
		if (trimmed === league.name) {
			setShowEditModal(false);
			return;
		}
		setIsSavingEdit(true);
		try {
			await leagueService.updateLeague(league.id, {
				name: trimmed,
				description: league.description || '',
			});
			toast.success(t('leagueDetails.leagueUpdated', { defaultValue: 'Liga byla upravena' }));
			window.location.reload();
		} catch (err) {
			console.error(err);
			toast.error(t('leagueDetails.editFailed', { defaultValue: 'Nepodařilo se upravit ligu' }));
		} finally {
			setIsSavingEdit(false);
			setShowEditModal(false);
		}
	};

	const handleConfirmLeave = async () => {
		setIsLeaving(true);
		try {
			await leagueService.leaveLeague(league.id);
			toast.success(t('leagues.successfullyLeftLeague', { defaultValue: 'Úspěšně jste opustili ligu!' }));
			window.location.href = '/dashboard';
		} catch (err) {
			console.error(err);
			toast.error(t('errors.failedToLeaveLeague', { defaultValue: 'Nepodařilo se opustit ligu' }));
		} finally {
			setIsLeaving(false);
			setShowLeaveModal(false);
		}
	};

	const modalContent = (
		<div className="invite-modal-overlay" onClick={() => setShowInviteModal(false)}>
			<div className="invite-modal invitation-modal" onClick={(e) => e.stopPropagation()}>

				<div className="invite-modal-header">
					<h3 className="invite-modal-title">
						{t('leagues.invitePlayers', { defaultValue: 'Pozvat hráče' })}
					</h3>
					<button
						onClick={() => setShowInviteModal(false)}
						className="invite-modal-close"
						aria-label="Close"
					>
						<FaTimes />
					</button>
				</div>

				<div className="invite-modal-body">
					{isLoadingLink ? (
						<p className="invite-loading-text">
							{t('generatingLink')}
						</p>
					) : (
						<>
							<p className="invite-description">
								{t('shareDescription')}
							</p>

							{navigator.share && (
								<button onClick={handleNativeShare} className="invite-btn-primary">
									<FaShareAlt className="invite-icon" />
									{t('share')}
								</button>
							)}

							<button onClick={handleCopyLink} className={navigator.share ? "invite-btn-secondary" : "invite-btn-primary"}>
								<FaCopy className="invite-icon" />
								{navigator.share
									? t('copyLinkOnly')
									: t('copyLink')
								}
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);

	const groupedStandings = standings.reduce((acc, row) => {
		const key = row.groupName || '';
		if (!acc[key]) acc[key] = [];
		acc[key].push(row);
		return acc;
	}, {});
	const groupKeys = Object.keys(groupedStandings);
	const showGroupHeaders = groupKeys.length > 1;

	const renderFormPills = (form) => {
		if (!form) return null;
		const chars = String(form).slice(-5).split('');
		return (
			<span className="standings-form">
				{chars.map((c, i) => {
					const upper = (c || '').toUpperCase();
					let cls = 'unknown';
					let label = upper;
					if (upper === 'W') {
						cls = 'win';
						label = t('win', { defaultValue: 'W' });
					} else if (upper === 'D') {
						cls = 'draw';
						label = t('draw', { defaultValue: 'D' });
					} else if (upper === 'L') {
						cls = 'loss';
						label = t('lost', { defaultValue: 'L' });
					}
					return (
						<span key={i} className={`standings-form-pill ${cls}`}>
							{label}
						</span>
					);
				})}
			</span>
		);
	};

	const renderStandingsTable = (rows) => (
		<table className="standings-table">
			<thead>
				<tr>
					<th className="rank-col">#</th>
					<th className="team-col">{t('standings.team', { defaultValue: 'Tým' })}</th>
					<th title={t('standings.played', { defaultValue: 'Zápasy' })}>P</th>
					<th title={t('standings.won', { defaultValue: 'Výhry' })}>V</th>
					<th title={t('standings.drawn', { defaultValue: 'Remízy' })}>R</th>
					<th title={t('standings.lost', { defaultValue: 'Prohry' })}>P</th>
					{!isMobile && (
						<>
							<th title={t('standings.goalsFor', { defaultValue: 'Vstřelené' })}>VG</th>
							<th title={t('standings.goalsAgainst', { defaultValue: 'Obdržené' })}>OG</th>
						</>
					)}
					<th title={t('standings.goalsDiff', { defaultValue: 'Rozdíl' })}>±</th>
					<th title={t('standings.points', { defaultValue: 'Body' })}>B</th>
					<th className="form-col" title={t('standings.form', { defaultValue: 'Forma' })}>
						{t('standings.formShort', { defaultValue: 'Forma' })}
					</th>
				</tr>
			</thead>
			<tbody>
				{rows.map((row, idx) => (
					<tr key={`${row.team?.id || row.rank}-${idx}`}>
						<td className="rank-col">{row.rank}</td>
						<td className="team-col">
							<div className="standings-team">
								<TeamBadge teamId={row.team?.id} teamName={row.team?.name} simple />
								<span className="standings-team-name">
									{(() => {
										if (!row.team?.name) return '';
										const name = t(`teams.${row.team.name}`, { defaultValue: row.team.name });
										if (isMobile && name.length > 12) {
											return `${name.slice(0, 11)}…`;
										}
										return name;
									})()}
								</span>
							</div>
						</td>
						<td>{row.played}</td>
						<td>{row.won}</td>
						<td>{row.drawn}</td>
						<td>{row.lost}</td>
						{!isMobile && (
							<>
								<td>{row.goalsFor}</td>
								<td>{row.goalsAgainst}</td>
							</>
						)}
						<td>{row.goalsDiff > 0 ? `+${row.goalsDiff}` : row.goalsDiff}</td>
						<td className="standings-points">{row.points}</td>
						<td className="form-col">{renderFormPills(row.form)}</td>
					</tr>
				))}
			</tbody>
		</table>
	);

	const standingsModalContent = (
		<div className="invite-modal-overlay" onClick={() => setShowStandingsModal(false)}>
			<div className="invite-modal standings-modal" onClick={(e) => e.stopPropagation()}>
				<div className="invite-modal-header">
					<h3 className="invite-modal-title">
						{t('standings.title', { defaultValue: 'Tabulka' })}
					</h3>
					<button
						onClick={() => setShowStandingsModal(false)}
						className="invite-modal-close"
						aria-label="Close"
					>
						<FaTimes />
					</button>
				</div>

				<div className="invite-modal-body standings-modal-body">
					{isLoadingStandings ? (
						<p className="invite-loading-text">
							{t('standings.loading', { defaultValue: 'Načítám tabulku…' })}
						</p>
					) : standings.length === 0 ? (
						<p className="invite-loading-text">
							{t('standings.empty', { defaultValue: 'Tabulka není zatím k dispozici.' })}
						</p>
					) : (
						<div className="standings-scroll">
							{showGroupHeaders ? (
								groupKeys.map((g) => (
									<div key={g || 'default'} className="standings-group">
										<h4 className="standings-group-title">{formatStandingsGroup(g, t)}</h4>
										{renderStandingsTable(groupedStandings[g])}
									</div>
								))
							) : (
								renderStandingsTable(standings)
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);

	const deleteModalContent = (
		<div className="invite-modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)}>
			<div className="invite-modal delete-modal" onClick={(e) => e.stopPropagation()}>
				<div className="invite-modal-header delete-header">
					<h3 className="invite-modal-title">
						{t('leagueDetails.deleteLeague', { defaultValue: 'Smazat ligu' })}
					</h3>
					<button
						onClick={() => setShowDeleteModal(false)}
						className="invite-modal-close"
						disabled={isDeleting}
					>
						<FaTimes />
					</button>
				</div>

				<div className="invite-modal-body">
					<p className="delete-description">
						{t('leagueDetails.confirmDelete', { defaultValue: 'Opravdu chcete smazat ligu?' })}
						<br />
						<strong>{league.name}</strong>
					</p>
					<p className="delete-subtext">
						{t('leagueDetails.deleteWarning', { defaultValue: 'Tato akce je nevratná a všechna data budou smazána.' })}
					</p>

					<div className="modal-footer-actions">
						<button
							onClick={() => setShowDeleteModal(false)}
							className="invite-btn-secondary"
							disabled={isDeleting}
						>
							{t('cancel', { defaultValue: 'Zrušit' })}
						</button>
						<button
							onClick={handleConfirmDelete}
							className="invite-btn-primary delete-confirm-btn"
							disabled={isDeleting}
						>
							{isDeleting ? t('common.deleting', { defaultValue: 'Mažu...' }) : t('common.delete', { defaultValue: 'Smazat navždy' })}
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className="league-header">
			<div className="header-left-content">
				<Link
					to={league.visibility === 'PUBLIC' && league.isMember === false ? '/public-leagues' : '/dashboard'}
					className="back-link"
				>
					<FaArrowLeft className="back-icon" />
				</Link>

				<h2 className="league-title" title={league.name}>
					{league.name}
				</h2>
			</div>

			<div
				className={`header-actions-fab ${isFabOpen ? 'open' : ''}`}
				onMouseEnter={() => setIsFabOpen(true)}
				onMouseLeave={() => setIsFabOpen(false)}
			>
				<div className="fab-menu" role="menu">
					<button
						className="fab-action invite-action"
						onClick={() => { setIsFabOpen(false); handleOpenInviteModal(); }}
						title={t('leagues.invitePlayers', { defaultValue: 'Pozvat hráče' })}
						aria-label={t('leagues.invitePlayers', { defaultValue: 'Pozvat hráče' })}
					>
						<FaUserPlus className="action-icon" />
						<span className="fab-tooltip">{t('leagues.invitePlayers', { defaultValue: 'Pozvat hráče' })}</span>
					</button>

					{isFootball && !isSingleTeamTournament && (
						<button
							className="fab-action standings-action"
							onClick={() => { setIsFabOpen(false); handleOpenStandingsModal(); }}
							title={t('standings.title', { defaultValue: 'Tabulka' })}
							aria-label={t('standings.title', { defaultValue: 'Tabulka' })}
						>
							<FaTable className="action-icon" />
							<span className="fab-tooltip">{t('standings.title', { defaultValue: 'Tabulka' })}</span>
						</button>
					)}

					{canLeave && !isOwner && (
						<button
							className="fab-action leave-action"
							onClick={() => { setIsFabOpen(false); setShowLeaveModal(true); }}
							title={t('leagues.leaveLeague', { defaultValue: 'Opustit ligu' })}
							aria-label={t('leagues.leaveLeague', { defaultValue: 'Opustit ligu' })}
						>
							<FaSignOutAlt className="action-icon" />
							<span className="fab-tooltip">{t('leagues.leaveLeague', { defaultValue: 'Opustit ligu' })}</span>
						</button>
					)}

					{isOwner && (
						<button
							className="fab-action edit-action"
							onClick={() => { setIsFabOpen(false); handleOpenEditModal(); }}
							title={t('leagueDetails.editLeague', { defaultValue: 'Upravit ligu' })}
							aria-label={t('leagueDetails.editLeague', { defaultValue: 'Upravit ligu' })}
						>
							<FaPen className="action-icon" />
							<span className="fab-tooltip">{t('leagueDetails.editLeague', { defaultValue: 'Upravit ligu' })}</span>
						</button>
					)}

					{isOwner && (
						<button
							className="fab-action delete-action"
							onClick={() => { setIsFabOpen(false); handleDeleteClick(); }}
							title={t('leagueDetails.deleteLeague', { defaultValue: 'Smazat ligu' })}
							aria-label={t('leagueDetails.deleteLeague', { defaultValue: 'Smazat ligu' })}
						>
							<MdDeleteForever className="action-icon" />
							<span className="fab-tooltip">{t('leagueDetails.deleteLeague', { defaultValue: 'Smazat ligu' })}</span>
						</button>
					)}
				</div>

				<button
					type="button"
					className="fab-trigger"
					onClick={() => setIsFabOpen((open) => !open)}
					aria-expanded={isFabOpen}
					aria-label={t('common.actions', { defaultValue: 'Akce' })}
				>
					<FaPlus className="fab-trigger-icon" />
				</button>
			</div>

			{showInviteModal && ReactDOM.createPortal(modalContent, document.body)}
			{showDeleteModal && ReactDOM.createPortal(deleteModalContent, document.body)}
			{showStandingsModal && ReactDOM.createPortal(standingsModalContent, document.body)}
			{showEditModal && ReactDOM.createPortal(
				<div className="invite-modal-overlay" onClick={() => !isSavingEdit && setShowEditModal(false)}>
					<div className="invite-modal edit-modal" onClick={(e) => e.stopPropagation()}>
						<div className="invite-modal-header">
							<h3 className="invite-modal-title">
								{t('leagueDetails.editLeague', { defaultValue: 'Upravit ligu' })}
							</h3>
							<button
								onClick={() => setShowEditModal(false)}
								className="invite-modal-close"
								disabled={isSavingEdit}
								aria-label="Close"
							>
								<FaTimes />
							</button>
						</div>
						<div className="invite-modal-body">
							<label className="edit-modal-label" htmlFor="league-edit-name">
								{t('leagueDetails.leagueName', { defaultValue: 'Název ligy' })}
							</label>
							<input
								id="league-edit-name"
								type="text"
								className="invite-link-input edit-modal-input"
								value={editName}
								onChange={(e) => setEditName(e.target.value)}
								maxLength={100}
								disabled={isSavingEdit}
								autoFocus
							/>
							<div className="modal-footer-actions">
								<button
									onClick={() => setShowEditModal(false)}
									className="modal-btn-cancel invite-btn-secondary"
									disabled={isSavingEdit}
								>
									{t('common.cancel', { defaultValue: 'Zrušit' })}
								</button>
								<button
									onClick={handleConfirmEdit}
									className="modal-btn-confirm invite-btn-primary"
									disabled={isSavingEdit || !editName.trim()}
								>
									{isSavingEdit ? t('common.processing', { defaultValue: 'Zpracovávám...' }) : t('common.save', { defaultValue: 'Uložit' })}
								</button>
							</div>
						</div>
					</div>
				</div>,
				document.body
			)}
			{showLeaveModal && ReactDOM.createPortal(
				<div className="invite-modal-overlay" onClick={() => !isLeaving && setShowLeaveModal(false)}>
					<div className="invite-modal leave-modal" onClick={(e) => e.stopPropagation()}>
						<div className="invite-modal-header">
							<h3 className="invite-modal-title">
								{t('leagues.leaveLeague', { defaultValue: 'Opustit ligu' })}
							</h3>
							<button
								onClick={() => setShowLeaveModal(false)}
								className="invite-modal-close"
								disabled={isLeaving}
								aria-label="Close"
							>
								<FaTimes />
							</button>
						</div>
						<div className="invite-modal-body">
							<p className="modal-description">
								{t('leagues.confirmLeave', { defaultValue: 'Opravdu chcete opustit ligu?' })}
								<br />
								<strong>{league.name}</strong>
							</p>
							<div className="modal-footer-actions">
								<button
									onClick={() => setShowLeaveModal(false)}
									className="modal-btn-cancel invite-btn-secondary"
									disabled={isLeaving}
								>
									{t('common.cancel', { defaultValue: 'Zrušit' })}
								</button>
								<button
									onClick={handleConfirmLeave}
									className="modal-btn-confirm btn-leave invite-btn-primary"
									disabled={isLeaving}
								>
									{isLeaving ? t('common.processing', { defaultValue: 'Zpracovávám...' }) : t('leagues.leaveLeague', { defaultValue: 'Opustit' })}
								</button>
							</div>
						</div>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default LeagueHeader;